import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/authService';
import Icon from '../AppIcon';

const Header = ({ onMenuToggle, isMenuOpen = false }) => {
  const navigate = useNavigate();
  const { user, userProfile, loading } = useAuth();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await authService?.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Sign out error:', error);
    } finally {
      setSigningOut(false);
      setShowProfileMenu(false);
    }
  };

  const handleProfileClick = () => {
    setShowProfileMenu(false);
    navigate('/profile');
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background border-b border-border">
      <div className="flex items-center justify-between h-16 px-4">
        {/* Left side - Menu toggle and branding */}
        <div className="flex items-center gap-4">
          <button
            onClick={onMenuToggle}
            className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors"
            aria-label="Toggle menu"
          >
            <Icon name={isMenuOpen ? "X" : "Menu"} size={20} />
          </button>
          
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="Database" size={18} className="text-primary-foreground" />
            </div>
            <div className="hidden sm:block">
              <h1 className="font-semibold text-foreground">ZentriDMS</h1>
              <p className="text-xs text-muted-foreground">Document Management</p>
            </div>
          </div>
        </div>

        {/* Right side - User menu */}
        {user ? (
          <div className="relative">
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors"
            >
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-foreground">
                  {userProfile?.fullName || user?.email?.split('@')?.[0] || 'User'}
                </p>
                <p className="text-xs text-muted-foreground capitalize">
                  {userProfile?.role || 'user'}
                </p>
              </div>
              
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                {userProfile?.avatarUrl ? (
                  <img 
                    src={userProfile?.avatarUrl} 
                    alt="Profile" 
                    className="w-8 h-8 rounded-full object-cover"
                  />
                ) : (
                  <Icon name="User" size={16} className="text-primary-foreground" />
                )}
              </div>
              
              <Icon 
                name={showProfileMenu ? "ChevronUp" : "ChevronDown"} 
                size={16} 
                className="text-muted-foreground" 
              />
            </button>

            {/* Profile dropdown */}
            {showProfileMenu && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-background border border-border rounded-lg shadow-lg py-2 z-50">
                <div className="px-4 py-2 border-b border-border">
                  <p className="font-medium text-foreground">
                    {userProfile?.fullName || 'User'}
                  </p>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                  {userProfile?.department && (
                    <p className="text-xs text-muted-foreground">{userProfile?.department}</p>
                  )}
                </div>
                
                <button
                  onClick={handleProfileClick}
                  className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-muted transition-colors"
                >
                  <Icon name="User" size={16} />
                  <span>Profile Settings</span>
                </button>
                
                <button
                  onClick={() => {
                    setShowProfileMenu(false);
                    navigate('/user-management');
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-muted transition-colors"
                >
                  <Icon name="Users" size={16} />
                  <span>User Management</span>
                </button>
                
                <div className="border-t border-border my-2"></div>
                
                <button
                  onClick={handleSignOut}
                  disabled={signingOut}
                  className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-muted transition-colors text-destructive disabled:opacity-50"
                >
                  <Icon name={signingOut ? "Loader" : "LogOut"} size={16} />
                  <span>{signingOut ? 'Signing out...' : 'Sign Out'}</span>
                </button>
              </div>
            )}
          </div>
        ) : loading ? (
          <div className="flex items-center gap-2">
            <Icon name="Loader" size={16} className="animate-spin text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Loading...</span>
          </div>
        ) : (
          <button
            onClick={() => navigate('/login')}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
          >
            Sign In
          </button>
        )}
      </div>
      {/* Close profile menu when clicking outside */}
      {showProfileMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowProfileMenu(false)}
        />
      )}
    </header>
  );
};

export default Header;